import { useEffect, useState } from 'react';
import { Box, IconButton, Modal, ModalDialog } from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { motion, usePresence, useReducedMotion } from 'framer-motion';
import { useWindowWidth } from '../hooks';

// Shared finance panel standard, copied from TasksPage's folder sidebar.
// Use this component for future side forms instead of a separate animation.
export const SIDE_PANEL_ANIMATION = {
  panel: { duration: .35, ease: [.4, 0, .2, 1], closeDelay: .15 },
  backdrop: { duration: .2 },
  closeButton: { duration: .25, delay: .22, ease: [.34, 1.56, .64, 1] },
};
export const SIDE_PANEL_HEADER = {
  bgcolor: 'neutral.50', boxShadow: 'md', borderRadius: 15,
  px: 2, py: 1, flexShrink: 0,
};

export default function FinanceSidePanel({ open = true, side = 'right', width = 440, mobileBreakpoint = 600, onClose, busy = false, closeLabel = 'Закрыть форму', label, children, sx }) {
  const viewport = useWindowWidth();
  const reduced = useReducedMotion();
  const [isPresent, safeToRemove] = usePresence();
  const [mounted, setMounted] = useState(open);
  const visible = open && isPresent;
  const mobile = viewport < mobileBreakpoint;
  const panelWidth = mobile ? Math.max(0, viewport - 62) : Math.min(width, viewport - 62);
  const direction = side === 'left' ? -1 : 1;
  const distance = direction * (panelWidth + 16);
  const panelTransition = { duration: reduced ? 0 : SIDE_PANEL_ANIMATION.panel.duration, ease: SIDE_PANEL_ANIMATION.panel.ease, delay: visible || reduced ? 0 : SIDE_PANEL_ANIMATION.panel.closeDelay };
  const buttonTransition = reduced ? { duration: 0 } : SIDE_PANEL_ANIMATION.closeButton;
  const closePosition = mobile ? 'calc(100vw - 52px)' : panelWidth + 16;

  useEffect(() => { if (open && isPresent) setMounted(true); }, [open, isPresent]);
  function closed() {
    if (!visible) { setMounted(false); safeToRemove?.(); }
  }
  if (!mounted) return null;

  return <Modal open hideBackdrop onClose={busy || !visible ? undefined : onClose}>
    <Box tabIndex={-1} sx={{ position: 'fixed', inset: 0, outline: 'none', pointerEvents: 'none' }}>
      <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: visible ? 1 : 0 }} exit={{ opacity: 0 }} transition={{ duration: reduced ? 0 : SIDE_PANEL_ANIMATION.backdrop.duration }}
        onClick={visible && !busy ? onClose : undefined} data-finance-backdrop="true"
        sx={{ position: 'fixed', inset: 0, zIndex: 0, backdropFilter: 'blur(4px)', bgcolor: 'rgba(0,0,0,.25)', pointerEvents: visible ? 'auto' : 'none' }} />
      <ModalDialog component={motion.div} aria-label={label} data-finance-panel={side} data-state={visible ? 'open' : 'closing'}
        initial={{ x: reduced ? 0 : distance }} animate={{ x: visible ? 0 : distance }} exit={{ x: distance }} transition={panelTransition} onAnimationComplete={closed}
        sx={{ position: 'fixed', top: 8, bottom: 8, left: side === 'left' ? 8 : 'auto', right: side === 'right' ? 8 : 'auto', transform: 'none', width: panelWidth, minWidth: 0, maxWidth: 'none', maxHeight: 'none', border: 0, borderRadius: 20, bgcolor: 'neutral.100', p: 1, gap: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'lg', zIndex: 1, pointerEvents: visible ? 'auto' : 'none', ...sx }}>
        {children}
      </ModalDialog>
      <Box component={motion.div}
        initial={{ x: reduced ? 0 : direction * 44, opacity: 0 }} animate={{ x: visible ? 0 : direction * 44, opacity: visible ? 1 : 0 }} exit={{ x: direction * 44, opacity: 0 }} transition={buttonTransition}
        sx={{ position: 'fixed', top: 20, [side === 'left' ? 'left' : 'right']: closePosition, [side === 'left' ? 'ml' : 'mr']: 1, zIndex: 2, pointerEvents: visible ? 'auto' : 'none' }}>
        <IconButton size="sm" variant="plain" disabled={busy || !visible} onClick={onClose} aria-label={closeLabel}
          sx={{ width: 36, height: 36, minWidth: 36, minHeight: 36, borderRadius: '50%', bgcolor: 'neutral.100', color: 'text.tertiary', transition: 'color .15s', '&:hover': { bgcolor: 'neutral.100', color: 'danger.plainColor' } }}><CloseRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
      </Box>
    </Box>
  </Modal>;
}
